import { useState, useEffect, useRef, useCallback } from 'react';
import { fetchAnimationCode } from '../services/api';
import { type IHttpClient } from '../services/contracts/IHttpClient';
import { CommandInvoker } from '../commands/command-invoker';
import { InitializeCoreCommand } from '../commands/core/initialize-core-command';
import { SignAnimationCodeCommand } from '../commands/core/sign-animation-codes-command';
import { DisplaySubtitleCommand } from '../commands/core/display-subtitle-command';
import { SetBackgroundColorCommand } from '../commands/core/set-background-color-command';
import { Avatar } from '../shared/types/avatar/types';
import { useCoreSentenceStore } from '../stores/core-sentence';
import { useExpirationStore } from '../stores/expiration';
import { logEventFactory } from '../usecases/factories/log-event-factory';
import { ETranslationEvents } from '../shared/enums/ETranslationEvents';

export const useTranslationLinks = (httpClient: IHttpClient) => {
  const [isPending, setIsPending] = useState(true);

  const [expired, setExpired] = useExpirationStore((state) => [
    state.expired,
    state.setExpired,
  ]);
  const [setHashSentence, setSentenceAnimation] = useCoreSentenceStore(
    (state) => [state.setSentence, state.setSentenceAnimation]
  );

  const urlParams = new URLSearchParams(window.location.search);
  const tokenParam = urlParams.get('t');

  const commandInvoker = useRef(new CommandInvoker()).current;

  const initializeCore = useCallback(
    async (avatar: Avatar) => {
      const avatarHolder = document.getElementById('avatar-holder');
      if (avatarHolder) {
        commandInvoker.addCommand(
          new InitializeCoreCommand(avatarHolder, avatar)
        );
        commandInvoker.addCommand(new SetBackgroundColorCommand('#DEDEDE'));
      }
    },
    [commandInvoker]
  );

  const verifyHashExpirationAndSign = useCallback(async () => {
    try {
      const res = await fetchAnimationCode(httpClient, tokenParam ?? '');
      const { translationLink } = res;
      const animations = translationLink.glosas.join('$');

      setSentenceAnimation(animations);
      setHashSentence(translationLink.sentence);

      await initializeCore(translationLink.avatar);
      commandInvoker.addCommand(new SignAnimationCodeCommand(animations));
      commandInvoker.addCommand(new DisplaySubtitleCommand(translationLink.sentence));
    } catch (err) {
      await initializeCore('hugo');
      setExpired(true);
    } finally {
      setIsPending(false);
      commandInvoker.executeCommands();
    }
  }, [
    commandInvoker,
    httpClient,
    initializeCore,
    setExpired,
    setHashSentence,
    setSentenceAnimation,
    tokenParam
  ]);

  useEffect(() => {
    verifyHashExpirationAndSign();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    logEventFactory.execute(ETranslationEvents.ClickTranslationLink, { translationLink: tokenParam });
  }, [tokenParam]);

  return {
    expired,
    isPending,
  };
};
